import React, {useRef, useMemo, useState} from 'react';
import { connect } from 'react-redux';
import {withStyles, Typography, Tab, Tabs, Paper, Button} from '@material-ui/core';
import {hasPermission, updateURL} from '../../sc-utilities';
import KPIList from './KPIList';
import {getTabValue, TabContainer, TabPanel} from "../../sc-order/Order/Details";
import Overview from "../Overview";
import SuccessRatio from "./SuccessRatio";

const styles = theme => ({
    "@global": {
        "[class*='buttonsCustomer']": {
            margin: theme.spacing(),
            fontSize: '13px',
            width: '100%',
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.buttonText.default
            },
        },
        "[class*='.MuiButton-root']": {
            minWidth: '24px',
            borderRadius: '1px'
        },
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.buttonText.default,
            margin: theme.spacing(),
            minWidth: '24px',
            borderRadius: '1px',
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
    },
    root: {
        width: `98.9%`,
        height: 'auto',
        zIndex: 1000,
        paddingTop: theme.spacing(),
        paddingLeft: theme.spacing(),
        marginBottom: theme.spacing(),
        marginTop: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    remote: {
        textDecoration: 'none',
        color: 'white'
    },
    chip_NotErased: {
        color: theme.palette.warning.dark,
        padding: 1,
        fontSize: 12
    },
    chip_InProgress: {
        color: theme.palette.customInProgress.dark,
        padding: 1,
        fontSize: 12
    },
    chip_Error: {
        color: theme.palette.error.dark,
        padding: 1,
        margin: 1,
        fontSize: 12
    },
    chip_Warning: {
        color: theme.palette.error.light,
        padding: 1,
        fontSize: 12
    },
    chip_Aborted: {
        color: theme.palette.aborted.main,
        padding: 1,
        fontSize: 12
    },
    chip_Erased: {
        color: theme.palette.success.dark,
        padding: 1,
        fontSize: 12
    },
    chip_Started: {
        color: theme.palette.primary.main,
        padding: 1,
        fontSize: 12
    },
    wipeProgress: {
        padding: '10px'
    },
    paletteButtons: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.buttonText.default,
        margin: theme.spacing(),
        minWidth: '24px',
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
    },
    linearColorPrimary: {
        backgroundColor: theme.palette.info.light,
    },
    linearBarColorPrimary: {
        backgroundColor: theme.palette.info.dark,
    },
    textField: {
        marginLeft: 10
    },

    storage_WipeMethod: {
        fontSize: 12
    },
    buttonsCustomer: {
        color: theme.palette.primary.main,
        margin: theme.spacing(1),
        fontSize: '13px',
        width: '100%',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
    },
});

const KPI = props => {
    const { classes, permissions, translations, columns, theme } = props;

    const [tabValue, setTabValue] = useState(getTabValue());
    const [refetchTab, setRefetchTab] = useState(false);

    const handleChange = (_event, value) => {
        updateURL(value);
        setTabValue(value);
    };

    const visible = columns?.['KPIOverview'] ?? null;
    const tableRef = useRef();

    const tableColumns = useMemo(() => {
        return [
            {
                title: translations["first_name"] || 'First Name', field: 'FirstName',
                hidden: visible ? visible['FirstName'] : false,
                filtering: false,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.FirstName ? rowData.FirstName : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["last_name"] || 'Last Name', field: 'LastName',
                hidden: visible ? visible['LastName'] : false,
                filtering: false,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.LastName ? rowData.LastName : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["username"] || 'Username', field: 'Username',
                hidden: visible ? visible['Username'] : false,
                filtering: false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Username ? translations[rowData.Username] || rowData.Username : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["total_processed_devices"] || 'Total processed devices', field: 'TotalProcessed',
                hidden: visible ? visible['TotalProcessed'] : false,
                filtering: false,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData?.TotalProcessed ?? (translations['short_not_assigned'] || 'n/a')}</Typography>;
                }
            },
            {
                title: translations["average_hourly_processed_devices"] || 'Average hourly processed devices', field: 'TotalProcessedHourly',
                hidden: visible ? visible['TotalProcessedHourly'] : false,
                filtering: false,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData?.TotalHourlyRate ?? (translations['short_not_assigned'] || 'n/a')}</Typography>;
                }
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible]);

    const refetch = () => setRefetchTab(true);

    const GenerateButton = () => (
        <Button aria-label={translations['generate'] || "Generate"} component="div"
                    variant={"contained"}
                    onClick={() => refetch()}>
            {translations['generate'] || "Generate"}
        </Button>
    )

    const tabProps = {
        refetchTab,
        setRefetchTab
    }

    return (
        <>
            <Paper className={classes.root}>
                <Tabs
                    value={tabValue}
                    onChange={handleChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"

                >
                    {hasPermission(permissions, 'overview') ? <Tab value={0} label={translations["overview"] || "System overview"} /> : null}
                    {hasPermission(permissions, 'statistic_throughput') ? <Tab value={1} label={translations['throughput'] || "Throughput"} /> : null}
                    {hasPermission(permissions, 'statistic_success_ratio') ? <Tab value={2} label={translations['success_ratio'] || "Success ratio"} /> : null}

                </Tabs>
            </Paper>

            {hasPermission(permissions, 'overview') &&
            <TabPanel index={0} value={tabValue} style={{ padding: 3 * 3 }}>
                <TabContainer dir={theme.direction}>
                    {GenerateButton()}
                    <Overview hideOptions fromKPI {...tabProps} />
                </TabContainer>
            </TabPanel>}

            {hasPermission(permissions, 'statistic_list') &&
            <TabPanel index={1} value={tabValue} style={{ padding: 3 * 3 }}>
                <TabContainer dir={theme.direction}>
                    {GenerateButton()}
                    <KPIList activeCustomer tableColumns={tableColumns} tableRef={tableRef} {...tabProps} />
                </TabContainer>
            </TabPanel>}

            {hasPermission(permissions, '') &&
                <TabPanel index={2} value={tabValue} style={{ padding: 3 * 3 }}>
                    <TabContainer dir={theme.direction}>
                        {GenerateButton()}
                        <SuccessRatio {...tabProps} />
                    </TabContainer>
                </TabPanel>}
        </>
    );
}

const mapDispatchToProps = dispatch => {
    return {
    };
};

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID || null,
        permissions: state?.core?.login?.permissions || [],
        columns: state?.core?.login?.userSettings?.Columns || null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {}
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(KPI));
